import { useMutation } from '@apollo/client';
import useAppSelector from '../../app/redux/useAppSelector';
import type {
  TopicPostsOrder,
  TopicPostsType,
} from '../../graphql/generated/graphql';
import topicPolicyCheck, { TopicPolicyAction } from '../topicPolicyCheck';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const PIN_POST_MUTATION = gql(/** @complexity 58 */ `
  mutation pinPost(
    $first: Int!
    $pinnedOrder: Int!
    $orderBy: TopicPostsOrder!
    $postId: ID!
    $topicId: ID!
    $type: TopicPostsType
  ) {
    pinPost(postId: $postId, topicId: $topicId, pinnedOrder: $pinnedOrder) {
      id
      posts(first: $first, orderBy: $orderBy, type: $type) {
        edges {
          cursor
          node {
            ...PostFields
          }
          pinnedOrder
        }
      }
    }
  }
`);

const UNPIN_POST_MUTATION = gql(/** @complexity 58 */ `
  mutation unpinPost(
    $first: Int!
    $orderBy: TopicPostsOrder!
    $postId: ID!
    $topicId: ID!
    $type: TopicPostsType
  ) {
    unpinPost(postId: $postId, topicId: $topicId) {
      id
      posts(first: $first, orderBy: $orderBy, type: $type) {
        edges {
          cursor
          node {
            ...PostFields
          }
          pinnedOrder
        }
      }
    }
  }
`);

const TOPIC_FRAGMENT = gql(`
  fragment usePinMutations_topic on Topic {
    id
    ...topicPolicyCheck_topic
  }
`);

const usePinMutations = ({
  topic,
  type,
  orderBy,
  postsCount,
}: {
  topic?: DocumentType<typeof TOPIC_FRAGMENT>;
  type: TopicPostsType | null;
  orderBy: TopicPostsOrder;
  postsCount: number;
}) => {
  const currentUser = useAppSelector((state) => state.session.user);

  const canPin = topic
    ? topicPolicyCheck(TopicPolicyAction.EditTopic, topic, currentUser)
    : false;

  const [pinPostMutate] = useMutation(PIN_POST_MUTATION);
  const [unpinPostMutate] = useMutation(UNPIN_POST_MUTATION);

  const pinPost =
    topic && canPin
      ? (postId: string, pinnedOrder: number) =>
          pinPostMutate({
            variables: {
              first: postsCount,
              orderBy,
              type,
              topicId: topic.id,
              postId,
              pinnedOrder,
            },
          })
      : null;

  const unpinPost =
    topic && canPin
      ? (postId: string) =>
          unpinPostMutate({
            variables: {
              first: postsCount,
              orderBy,
              type,
              topicId: topic.id,
              postId,
            },
          })
      : null;

  return { pinPost, unpinPost };
};

export default usePinMutations;
